// src/components/pcr/sections/Pathophysiology.tsx
import React from 'react';
import { useForm } from '../../../contexts/FormContext';
import SectionHeading from '../common/SectionHeading';

const Pathophysiology: React.FC = () => {
  const { formData, updateFormField } = useForm();
  const { pathophysiology } = formData;

  const handleChange = (pathway: string, value: string) => {
    updateFormField('pathophysiology', pathway, value);
  };

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-white p-4 rounded-lg mb-6 shadow-md">
      <SectionHeading 
        title="Pathophysiology" 
        description={`
        Provide an Overview: Describe the main pathophysiological mechanisms involved in each condition you are choosing.

        Pathophysiological Process: Clearly describe the primary pathophysiological mechanisms involved. Detail how normal physiological functions are altered, and explain the progression of the disease at the cellular and systemic levels.

        Mechanisms and Impact: Describe the key processes that lead to the changes in the body, focusing on any biochemical, inflammatory, or immune responses involved. Explain how these responses contribute to the onset and progression of the disease.

        Include References: Cite recent, evidence-based literature to support your explanations.`}
      />
      
      <div className="space-y-3">
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <label htmlFor="pathophysiology-pathway-1" className="block text-sm font-medium text-gray-700">Pathway 1</label>
              <span className="text-xs text-gray-500">{countWords(pathophysiology.pathway1)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mb-2">(250-500 words)</p>
            <textarea 
              id="pathophysiology-pathway-1" 
              className="text-xs md:text-base p-2 mt-1 block w-full rounded-md border-2 border-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px]" 
              value={pathophysiology.pathway1}
              onChange={(e) => handleChange('pathway1', e.target.value)}
              aria-labelledby="label-pathophysiology-pathway-1"
            />
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <label htmlFor="pathophysiology-pathway-2" className="block text-sm font-medium text-gray-700">Pathway 2</label>
              <span className="text-xs text-gray-500">{countWords(pathophysiology.pathway2)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mb-2">(250-500 words)</p>
            <textarea 
              id="pathophysiology-pathway-2" 
              className="text-xs md:text-base p-2 mt-1 block w-full rounded-md border-2 border-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px]" 
              value={pathophysiology.pathway2}
              onChange={(e) => handleChange('pathway2', e.target.value)}
              aria-labelledby="label-pathophysiology-pathway-2"
            />
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <label htmlFor="pathophysiology-pathway-3" className="block text-sm font-medium text-gray-700">Pathway 3</label>
              <span className="text-xs text-gray-500">{countWords(pathophysiology.pathway3)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mb-2">(250-500 words)</p>
            <textarea 
              id="pathophysiology-pathway-3" 
              className="text-xs md:text-base p-2 mt-1 block w-full rounded-md border-2 border-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px]" 
              value={pathophysiology.pathway3}
              onChange={(e) => handleChange('pathway3', e.target.value)}
              aria-labelledby="label-pathophysiology-pathway-3"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pathophysiology;